import React, { useState } from 'react';
import {
  EditOutlined,
  LineOutlined,
  BorderOutlined,
  DashOutlined,
  MinusOutlined,
  RadiusSettingOutlined,
  SelectOutlined,
  FontSizeOutlined,
  UndoOutlined,
  RedoOutlined,
  ClearOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { Tooltip, Upload } from 'antd';

const PALETTE = [
  '#1a1a1a',
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#10b981',
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#ffffff',
  '#94a3b8',
];

const STAMPS = [
  '😀',
  '😂',
  '❤️',
  '👍',
  '🔥',
  '⭐',
  '✅',
  '❌',
  '💡',
  '🎯',
  '🚀',
  '⚠️',
  '🎉',
  '💯',
  '🤔',
  '👏',
  '📌',
  '🔑',
  '💬',
  '🏆',
];

const PEN_SIZES = [
  { label: 'S', size: 2, dotR: 3 },
  { label: 'M', size: 5, dotR: 5 },
  { label: 'L', size: 10, dotR: 8 },
];

const LINE_STYLES = [
  {
    key: 'solid-straight',
    label: 'Straight',
    dash: [],
    tension: 0,
    straight: true,
    icon: (
      <line
        x1="3"
        y1="12"
        x2="21"
        y2="12"
        stroke="currentColor"
        strokeWidth="2"
      />
    ),
  },
  {
    key: 'solid-free',
    label: 'Solid',
    dash: [],
    tension: 0,
    straight: false,
    icon: <MinusOutlined style={{ fontSize: 15 }} />,
  },
  {
    key: 'curved',
    label: 'Curved',
    dash: [],
    tension: 0.5,
    straight: false,
    icon: <RadiusSettingOutlined style={{ fontSize: 15 }} />,
  },
  {
    key: 'dashed',
    label: 'Dashed',
    dash: [10, 5],
    tension: 0,
    straight: false,
    icon: <DashOutlined style={{ fontSize: 15 }} />,
  },
  {
    key: 'dotted',
    label: 'Dotted',
    dash: [2, 6],
    tension: 0,
    straight: false,
    icon: <span style={{ fontSize: 13, letterSpacing: 1 }}>···</span>,
  },
];

const SHAPES = [
  {
    type: 'rect',
    label: 'Rectangle',
    svg: (
      <rect
        x="2"
        y="4"
        width="20"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        rx="2"
      />
    ),
  },
  {
    type: 'circle',
    label: 'Circle',
    svg: (
      <circle
        cx="12"
        cy="12"
        r="9"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'triangle',
    label: 'Triangle',
    svg: (
      <polygon
        points="12,3 22,21 2,21"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'diamond',
    label: 'Diamond',
    svg: (
      <polygon
        points="12,3 21,12 12,21 3,12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'parallelogram-right',
    label: 'Parallelogram',
    svg: (
      <polygon
        points="6,5 20,5 18,19 4,19"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'heart',
    label: 'Heart',
    svg: (
      <path
        d="M12 21s-6-4.35-9-8.5C-1 7 3 3 7 5c2 1 3 3 5 5 2-2 3-4 5-5 4-2 8 2 4 7.5C18 16.65 12 21 12 21z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'parallelogram-left',
    label: 'Para. Left',
    svg: (
      <polygon
        points="4,5 18,5 20,19 6,19"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'oval',
    label: 'Oval',
    svg: (
      <ellipse
        cx="12"
        cy="12"
        rx="10"
        ry="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow Right',
    direction: 'right',
    svg: (
      <>
        <line
          x1="3"
          y1="12"
          x2="19"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="15,8 21,12 15,16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow Left',
    direction: 'left',
    svg: (
      <>
        <line
          x1="21"
          y1="12"
          x2="5"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="9,8 3,12 9,16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow Up',
    direction: 'up',
    svg: (
      <>
        <line
          x1="12"
          y1="21"
          x2="12"
          y2="5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="8,9 12,3 16,9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow Down',
    direction: 'down',
    svg: (
      <>
        <line
          x1="12"
          y1="3"
          x2="12"
          y2="19"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="8,15 12,21 16,15"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow ↔',
    direction: 'right',
    double: true,
    svg: (
      <>
        <line
          x1="5"
          y1="12"
          x2="19"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="9,8 3,12 9,16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="15,8 21,12 15,16"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'arrow',
    label: 'Arrow ↕',
    direction: 'down',
    double: true,
    svg: (
      <>
        <line
          x1="12"
          y1="5"
          x2="12"
          y2="19"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="8,9 12,3 16,9"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <polyline
          points="8,15 12,21 16,15"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </>
    ),
  },
  {
    type: 'preparation',
    label: 'Preparation',
    svg: (
      <polygon
        points="6,3 18,3 22,12 18,21 6,21 2,12"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    ),
  },
];

const Divider = () => (
  <div
    style={{
      width: 1,
      height: 24,
      background: '#e2e8f0',
      margin: '0 4px',
      flexShrink: 0,
    }}
  />
);

const BoardToolbar = ({
  tool,
  setTool,
  lineStyle,
  setLineStyle,
  shapeType,
  setShapeType,
  strokeColor,
  setStrokeColor,
  penSize,
  setPenSize,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  onStamp,
  onImageUpload,
  shapeConfig,
}) => {
  const [showLineMenu, setShowLineMenu] = useState(false);
  const [showShapeMenu, setShowShapeMenu] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showStamps, setShowStamps] = useState(false);
  const [showPenMenu, setShowPenMenu] = useState(false);

  const closeAll = () => {
    setShowLineMenu(false);
    setShowShapeMenu(false);
    setShowColorPicker(false);
    setShowStamps(false);
    setShowPenMenu(false);
  };

  const btn = (isActive, color = '#3b82f6') => ({
    padding: '6px 8px',
    borderRadius: 8,
    background: isActive ? '#eff6ff' : 'transparent',
    border: isActive ? `1px solid #bfdbfe` : '1px solid transparent',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.15s',
    position: 'relative',
  });

  const iconColor = (t) => (tool === t ? '#3b82f6' : '#374151');

  const popover = (children) => ({
    position: 'absolute',
    bottom: 52,
    left: '50%',
    transform: 'translateX(-50%)',
    background: '#fff',
    borderRadius: 10,
    boxShadow: '0 4px 20px rgba(0,0,0,0.13)',
    border: '1px solid #e2e8f0',
    zIndex: 2000,
    ...children,
  });

  const isShapeActive = (shape) => {
    if (shape.type !== shapeType) return false;
    if (shape.type === 'arrow') {
      return (
        shape.direction === shapeConfig?.direction &&
        !!shape.double === !!shapeConfig?.double
      );
    }
    return true;
  };

  const currentLineStyle =
    LINE_STYLES.find(
      (s) =>
        JSON.stringify(s.dash) === JSON.stringify(lineStyle.dash) &&
        s.tension === lineStyle.tension &&
        s.straight === lineStyle.straight,
    ) || LINE_STYLES[0];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: '#ffffff',
        padding: '8px 14px',
        borderRadius: 14,
        boxShadow: '0 4px 24px rgba(0,0,0,0.12), 0 1px 4px rgba(0,0,0,0.06)',
        display: 'flex',
        gap: 3,
        zIndex: 1000,
        alignItems: 'center',
        border: '1px solid #e2e8f0',
        userSelect: 'none',
      }}
    >
      <Tooltip title="Undo (Ctrl+Z)">
        <button
          style={{
            ...btn(false),
            opacity: canUndo ? 1 : 0.35,
            cursor: canUndo ? 'pointer' : 'not-allowed',
          }}
          onClick={onUndo}
          disabled={!canUndo}
        >
          <UndoOutlined style={{ fontSize: 17, color: '#374151' }} />
        </button>
      </Tooltip>
      <Tooltip title="Redo (Ctrl+Y)">
        <button
          style={{
            ...btn(false),
            opacity: canRedo ? 1 : 0.35,
            cursor: canRedo ? 'pointer' : 'not-allowed',
          }}
          onClick={onRedo}
          disabled={!canRedo}
        >
          <RedoOutlined style={{ fontSize: 17, color: '#374151' }} />
        </button>
      </Tooltip>

      <Divider />

      <Tooltip title="Select">
        <button
          style={btn(tool === 'select')}
          onClick={() => {
            setTool('select');
            closeAll();
          }}
        >
          <SelectOutlined
            style={{ fontSize: 18, color: iconColor('select') }}
          />
        </button>
      </Tooltip>

      <Tooltip title="Eraser">
        <button
          style={btn(tool === 'eraser')}
          onClick={() => {
            setTool('eraser');
            closeAll();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M20 20H7L3 16l10-10 7 7-2.5 2.5"
              stroke={tool === 'eraser' ? '#3b82f6' : '#374151'}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M6.5 17.5l4-4"
              stroke={tool === 'eraser' ? '#3b82f6' : '#374151'}
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </Tooltip>

      <Divider />

      <div style={{ position: 'relative' }}>
        <Tooltip title="Pen">
          <button
            style={btn(tool === 'pen')}
            onClick={() => {
              setTool('pen');
              setShowPenMenu((v) => !v);
              setShowLineMenu(false);
              setShowShapeMenu(false);
              setShowColorPicker(false);
              setShowStamps(false);
            }}
          >
            <EditOutlined style={{ fontSize: 18, color: iconColor('pen') }} />
            <div
              style={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                width: penSize === 2 ? 4 : penSize === 5 ? 6 : 8,
                height: penSize === 2 ? 4 : penSize === 5 ? 6 : 8,
                borderRadius: '50%',
                background: strokeColor,
                border: '1px solid #e2e8f0',
              }}
            />
          </button>
        </Tooltip>

        {showPenMenu && (
          <div
            style={{
              ...popover({ padding: '10px 12px' }),
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {PEN_SIZES.map(({ label, size, dotR }) => (
              <Tooltip key={label} title={`${label} — ${size}px`}>
                <div
                  onClick={() => {
                    setPenSize(size);
                    setShowPenMenu(false);
                  }}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: penSize === size ? '#eff6ff' : 'transparent',
                    border:
                      penSize === size
                        ? '1px solid #bfdbfe'
                        : '1px solid #e2e8f0',
                    transition: 'all 0.12s',
                  }}
                >
                  <div
                    style={{
                      width: dotR * 2,
                      height: dotR * 2,
                      borderRadius: '50%',
                      background: '#374151',
                    }}
                  />
                </div>
              </Tooltip>
            ))}
          </div>
        )}
      </div>

      <div style={{ position: 'relative' }}>
        <Tooltip title="Line">
          <button
            style={btn(tool === 'line')}
            onClick={() => {
              setTool('line');
              setShowLineMenu((v) => !v);
              setShowPenMenu(false);
              setShowShapeMenu(false);
              setShowColorPicker(false);
              setShowStamps(false);
            }}
          >
            <LineOutlined style={{ fontSize: 18, color: iconColor('line') }} />
          </button>
        </Tooltip>

        {showLineMenu && (
          <div
            style={{
              ...popover({ padding: '8px 10px' }),
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minWidth: 150,
            }}
          >
            {LINE_STYLES.map((ls) => (
              <div
                key={ls.key}
                onClick={() => {
                  setLineStyle({
                    dash: ls.dash,
                    tension: ls.tension,
                    straight: ls.straight,
                  });
                  setShowLineMenu(false);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '7px 10px',
                  borderRadius: 7,
                  cursor: 'pointer',
                  background:
                    currentLineStyle.key === ls.key ? '#eff6ff' : 'transparent',
                  color:
                    currentLineStyle.key === ls.key ? '#3b82f6' : '#374151',
                  transition: 'background 0.12s',
                  fontSize: 13,
                }}
              >
                <span
                  style={{
                    width: 20,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {ls.key === 'solid-straight' ? (
                    <svg width="18" height="14" viewBox="0 0 24 14">
                      <line
                        x1="1"
                        y1="7"
                        x2="23"
                        y2="7"
                        stroke="currentColor"
                        strokeWidth="2"
                      />
                    </svg>
                  ) : (
                    ls.icon
                  )}
                </span>
                {ls.label}
              </div>
            ))}
          </div>
        )}
      </div>

      <Divider />

      <div style={{ position: 'relative' }}>
        <Tooltip title="Shapes">
          <button
            style={btn(tool === 'shape')}
            onClick={() => {
              setTool('shape');
              setShowShapeMenu((v) => !v);
              setShowLineMenu(false);
              setShowPenMenu(false);
              setShowColorPicker(false);
              setShowStamps(false);
            }}
          >
            <BorderOutlined
              style={{ fontSize: 18, color: iconColor('shape') }}
            />
          </button>
        </Tooltip>

        {showShapeMenu && (
          <div
            style={{
              ...popover({
                padding: 12,
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 36px)',
                gap: 8,
                justifyItems: 'center',
              }),
            }}
          >
            {SHAPES.map(( shape ) => {
              const { type, label, svg } = shape;
              return (
                <Tooltip key={label} title={label}>
                  <div
                    onClick={() => {
                      setShapeType(type, shape.direction, shape.double);
                      setShowShapeMenu(false);
                    }}
                    style={{
                      width: 32,
                      height: 32,
                      cursor: 'pointer',
                      borderRadius: 6,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    background: (shapeType === type && shape.direction === shapeConfig?.direction && !!shape.double === !!shapeConfig?.double)
            ? '#eff6ff' : 'transparent',
                      border:
                        shapeType === type
                          ? '1px solid #bfdbfe'
                          : '1px solid transparent',
                      color: '#374151',
                      transition: 'all 0.12s',
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      {svg}
                    </svg>
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>

      <Divider />

      <Tooltip title="Sticky Note">
        <button
          style={btn(tool === 'sticky')}
          onClick={() => {
            setTool('sticky');
            closeAll();
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <rect
              x="3"
              y="3"
              width="15"
              height="18"
              rx="2"
              stroke={tool === 'sticky' ? '#f59e0b' : '#374151'}
              strokeWidth="1.8"
            />
            <path
              d="M18 15l-3 6v-6h3z"
              fill={tool === 'sticky' ? '#f59e0b' : '#374151'}
            />
            <line
              x1="7"
              y1="8"
              x2="14"
              y2="8"
              stroke={tool === 'sticky' ? '#f59e0b' : '#374151'}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="7"
              y1="12"
              x2="14"
              y2="12"
              stroke={tool === 'sticky' ? '#f59e0b' : '#374151'}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </Tooltip>

      <Tooltip title="Text Box">
        <button
          style={btn(tool === 'textbox')}
          onClick={() => {
            setTool('textbox');
            closeAll();
          }}
        >
          <FontSizeOutlined
            style={{ fontSize: 18, color: iconColor('textbox') }}
          />
        </button>
      </Tooltip>

      <div style={{ position: 'relative' }}>
        <Tooltip title="Stamps / Emoji">
          <button
            style={btn(tool === 'stamp')}
            onClick={() => {
              setTool('stamp');
              setShowStamps((v) => !v);
              setShowLineMenu(false);
              setShowShapeMenu(false);
              setShowColorPicker(false);
              setShowPenMenu(false);
            }}
          >
            <span style={{ fontSize: 17, lineHeight: 1 }}>😀</span>
          </button>
        </Tooltip>

        {showStamps && (
          <div
            style={{
              ...popover({
                padding: 10,
                display: 'grid',
                gridTemplateColumns: 'repeat(5, 34px)',
                gap: 4,
                justifyItems: 'center',
                minWidth: 190,
              }),
            }}
          >
            {STAMPS.map((emoji) => (
              <div
                key={emoji}
                onClick={() => {
                  onStamp(emoji);
                  setShowStamps(false);
                }}
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 6,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 20,
                  transition: 'background 0.1s',
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = '#f1f5f9')
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = 'transparent')
                }
              >
                {emoji}
              </div>
            ))}
          </div>
        )}
      </div>

      <Divider />

      <Tooltip title="Upload Image">
        <Upload
          accept="image/*"
          showUploadList={false}
          beforeUpload={onImageUpload}
        >
          <button style={btn(false)}>
            <PictureOutlined style={{ fontSize: 18, color: '#374151' }} />
          </button>
        </Upload>
      </Tooltip>

      {/* <div style={{ position: 'relative' }}>
        <Tooltip title="Color">
          <button
            style={{ ...btn(showColorPicker), gap: 6 }}
            onClick={() => {
              setShowColorPicker((v) => !v);
              setShowLineMenu(false);
              setShowShapeMenu(false);
              setShowStamps(false);
              setShowPenMenu(false);
            }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: '50%',
                background: strokeColor,
                border: '2px solid #e2e8f0',
                flexShrink: 0,
              }}
            />
            <span style={{ fontSize: 12, color: '#64748b' }}>Color</span>
          </button>
        </Tooltip>

        {showColorPicker && (
          <div
            style={{
              ...popover({ padding: 10 }),
              display: 'flex',
              gap: 8,
              flexWrap: 'wrap',
              width: 170,
            }}
          >
            {PALETTE.map((c) => (
              <div
                key={c}
                onClick={() => {
                  setStrokeColor(c);
                  setShowColorPicker(false);
                }}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: c,
                  cursor: 'pointer',
                  border:
                    strokeColor === c
                      ? '2px solid #3b82f6'
                      : '2px solid #e2e8f0',
                  outline: strokeColor === c ? '2px solid #bfdbfe' : 'none',
                  boxSizing: 'border-box',
                  transition: 'all 0.1s',
                }}
              />
            ))}
            <div
              style={{
                width: '100%',
                borderTop: '1px solid #e2e8f0',
                paddingTop: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span style={{ fontSize: 11, color: '#94a3b8' }}>Custom</span>
              <input
                type="color"
                value={strokeColor}
                onChange={(e) => setStrokeColor(e.target.value)}
                style={{
                  width: 28,
                  height: 22,
                  border: 'none',
                  padding: 0,
                  borderRadius: 4,
                  cursor: 'pointer',
                }}
              />
            </div>
          </div>
        )}
      </div> */}
    </div>
  );
};

export default BoardToolbar;
